import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { capsulesAPI, formatApiError } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, Plus, Boxes, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import PurgeCapsuleDialog from '@/components/PurgeCapsuleDialog';
import BorradoOperativoTotal from '@/components/BorradoOperativoTotal';

const VERTICALS = [
  { value: 'sistemas', label: 'Sistemas' },
  { value: 'compras', label: 'Compras' },
  { value: 'rrhh', label: 'RRHH' },
  { value: 'inmobiliaria', label: 'Inmobiliaria' },
  { value: 'comercial', label: 'Comercial' },
  { value: 'custom', label: 'Custom' },
];

export default function CapsulesPage() {
  const { user } = useAuth();
  const tenantId = user?.tenant_id || '';
  const isAdmin = user?.role === 'admin';
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [capsuleId, setCapsuleId] = useState('');
  const [name, setName] = useState('');
  const [vertical, setVertical] = useState('sistemas');
  const [purgeTarget, setPurgeTarget] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await capsulesAPI.list(tenantId);
      setList(res.data || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    load();
  }, [load]);

  const handleCreate = async (e) => {
    e.preventDefault();
    const slug = capsuleId.trim().toLowerCase().replace(/\s+/g, '-');
    if (!slug || !name.trim()) {
      toast.error('Indica ID de capsula (slug) y nombre');
      return;
    }
    setCreating(true);
    try {
      await capsulesAPI.create(tenantId, {
        capsule_id: slug,
        name: name.trim(),
        description: '',
        vertical,
      });
      toast.success('Capsula creada');
      setCapsuleId('');
      setName('');
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setCreating(false);
    }
  };

  if (loading && !list.length) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-6 h-6 animate-spin text-brand-primary" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-3xl mx-auto" data-testid="capsules-page">
      <div className="flex items-center gap-2 mb-6">
        <Boxes className="w-6 h-6 text-brand-primary" />
        <div>
          <h1 className="text-xl font-bold tracking-tight text-[#09090B]">Capsulas</h1>
          <p className="text-xs text-zinc-500 mt-0.5">Listado y alta por tenant (admin/supervisor)</p>
        </div>
      </div>

      <Card className="rounded-sm border-zinc-200 mb-6">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold uppercase tracking-wider">Nueva capsula</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleCreate} className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">ID capsula (slug)</Label>
                <Input
                  value={capsuleId}
                  onChange={(e) => setCapsuleId(e.target.value)}
                  placeholder="ej. compras-norte"
                  className="rounded-sm text-sm mt-1"
                  data-testid="capsule-id-input"
                />
              </div>
              <div>
                <Label className="text-xs">Nombre visible</Label>
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Nombre"
                  className="rounded-sm text-sm mt-1"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs">Vertical</Label>
              <select
                value={vertical}
                onChange={(e) => setVertical(e.target.value)}
                className="mt-1 w-full border border-zinc-200 rounded-sm px-2 py-2 text-sm bg-white"
              >
                {VERTICALS.map((v) => (
                  <option key={v.value} value={v.value}>
                    {v.label}
                  </option>
                ))}
              </select>
            </div>
            <Button type="submit" size="sm" disabled={creating} className="rounded-sm">
              {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />}
              Crear
            </Button>
          </form>
        </CardContent>
      </Card>

      <Card className="rounded-sm border-zinc-200">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold uppercase tracking-wider">Capsulas del tenant</CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="divide-y divide-zinc-100">
            {list.map((c) => (
              <li key={c.capsule_id} className="py-3 flex justify-between items-center text-sm gap-2">
                <div>
                  <span className="font-mono text-brand-primary">{c.capsule_id}</span>
                  <span className="text-zinc-600 ml-2">{c.name}</span>
                  {c.vertical && <span className="text-zinc-400 text-xs ml-2">({c.vertical})</span>}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {isAdmin && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-8 text-xs text-red-700 hover:text-red-900 hover:bg-red-50 rounded-sm"
                      onClick={() => setPurgeTarget({ capsule_id: c.capsule_id, name: c.name })}
                    >
                      <Trash2 className="w-3.5 h-3.5 mr-1" />
                      Eliminar
                    </Button>
                  )}
                  <Link
                    to={`/capsule-config?capsule=${encodeURIComponent(c.capsule_id)}`}
                    className="text-xs text-brand-primary hover:underline shrink-0"
                  >
                    Config cápsula
                  </Link>
                </div>
              </li>
            ))}
            {!list.length && <li className="py-4 text-zinc-500 text-sm">No hay capsulas.</li>}
          </ul>
        </CardContent>
      </Card>

      {/* **Solo admin**, y abajo de todo. Es lo mas destructivo del producto: no comparte fila con
          el alta de una capsula. `isAdmin` ya existe en esta pantalla; el backend lo exige igual
          —`require_role("admin")`— asi que esconderlo es cortesia, no el control. */}
      {isAdmin && (
        <div className="mt-6">
          <BorradoOperativoTotal tenantId={tenantId} onSuccess={load} />
        </div>
      )}

      <PurgeCapsuleDialog
        open={!!purgeTarget}
        onOpenChange={(open) => { if (!open) setPurgeTarget(null); }}
        tenantId={tenantId}
        capsuleId={purgeTarget?.capsule_id || ''}
        capsuleName={purgeTarget?.name}
        onSuccess={async () => {
          setPurgeTarget(null);
          await load();
        }}
      />
    </div>
  );
}
